import React, { useLayoutEffect, useRef } from 'react';
import './Section2.css';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import girlStanding from '../../../assets/images/GirlStanding1.png';
import girlJogging from '../../../assets/images/GirlStandingJogging1.png';
import standAndLook from '../../../assets/images/Standandlook1.png';

gsap.registerPlugin(ScrollTrigger);

const reasons = [
  { number: 1, title: 'Association with getting into trouble', text: 'In many sports, running is used to show that you are in trouble rather than to something to bring you enjoyment.', image: standAndLook, imageAlt: 'Silhouette of a person standing and looking', desktopImageClass: 'img-stand-look-1' },
  { number: 2, title: 'Perceived Lack of Fun', text: 'Traditional running drills can feel repetitive and less engaging, especially compared to team sports with immediate rewards.', image: standAndLook, imageAlt: 'Silhouette of a person standing and looking', desktopImageClass: 'img-stand-look-2' },
  { number: 3, title: 'Fear of Failure or Judgment', text: 'Youth may feel self-conscious about their speed or body type, avoiding running to prevent embarrassment.', image: standAndLook, imageAlt: 'Silhouette of a person standing and looking', desktopImageClass: 'img-stand-look-3' },
  { number: 4, title: 'Unrealistic Expectations', text: 'Many believe running is only for elite athletes, making it feel inaccessible to beginners.', image: girlJogging, imageAlt: 'Silhouette of a person jogging', desktopImageClass: 'img-jogging' },
  { number: 5, title: 'Physical Discomfort', text: 'Without proper pacing and guidance, new runners often experience discomfort, making running feel painful.', image: null, imageAlt: '', desktopImageClass: '' },
  { number: 6, title: 'Lack of Exposure to non-competitive Running', text: 'Many youth programs focus on racing, rather than running for health, enjoyment, and stress relief.', image: girlStanding, imageAlt: 'Silhouette of a person standing', desktopImageClass: 'img-standing' }
];

function Section2() {
  const sectionRef = useRef(null);
  const contentRef = useRef(null);
  const itemRefs = useRef([]);

  useLayoutEffect(() => {
    // This line is important to make sure the refs array is ready
    itemRefs.current = itemRefs.current.slice(0, reasons.length);
    
    let ctx = gsap.context(() => {
      ScrollTrigger.matchMedia({
        // --- 1. DESKTOP ANIMATION ("Scroll-Pin") ---
        "(min-width: 1280px)": function () {
          const items = itemRefs.current;

          // Timeline for pinned animation
          const pinTimeline = gsap.timeline({
            scrollTrigger: {
              trigger: sectionRef.current,
              pin: true,
              start: "top top",
              // --- FIX 1: Use a fixed value like our other sections ---
              end: "+=2500", // (This provides a smooth, consistent scroll)
              scrub: 1,
              // --- FIX 2: Removed 'once: true' ---
              // The 'onLeave' function will handle this properly.
              toggleActions: "play reverse play reverse",

              // --- When section scroll ends ---
              onLeave: self => {
                // Complete timeline gracefully
                gsap.to(self.animation, { progress: 1, duration: 0.5, ease: "power1.out" });

                // Stop listening for scroll updates and remove pin transform
                self.disable();
                self.pin && self.pin.style.removeProperty("transform");

                // Refresh ScrollTrigger to snap next section
                ScrollTrigger.refresh();
              }
            }
          });

          // Animate each reason item as the user scrolls
          items.forEach((item, index) => {
            pinTimeline.fromTo(
              item,
              { opacity: 0, y: 50 },
              { opacity: 1, y: 0, duration: 1, ease: 'power2.out' },
              `+=${index === 0 ? 0 : 0.5}` // Stagger items
            );
          });
        },

        // --- 2. TABLET & MOBILE ANIMATION ("Waterfall") ---
        "(max-width: 1279px)": function () {
          gsap.fromTo( // <-- Changed from .to() to .fromTo()
            itemRefs.current,
            { opacity: 0, y: 50 }, // <-- Added FROM state
            {
              opacity: 1,
              y: 0, // <-- FIX 3: Animate to y: 0
              duration: 0.8,
              ease: 'power2.out',
              stagger: 0.2,
              scrollTrigger: {
                trigger: sectionRef.current,
                start: "top 80%",
                toggleActions: "play none none none",
                once: true // Run only once
              }
            }
          );
        }
      });
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section className="section-2" ref={sectionRef}>
      {/* Left column (image for tablets) */}
      <div className="section-2-tablet-image-container">
        <img src={girlJogging} alt="Girl jogging" />
      </div>

      {/* Right column (text content) */}
      <div className="section-2-content" ref={contentRef}>
        {reasons.map((reason, index) => (
          <div
            className="reason-item"
            key={reason.number}
            ref={el => (itemRefs.current[index] = el)}
          >
            <div className="reason-text">
              <h3 className="reason-title">{reason.number}. {reason.title}</h3>
              <p className="reason-description">{reason.text}</p>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

export default Section2;